"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Star, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useParams } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import useUserStore from "@/features/auth/store/useUserStore";
import { axiosApiInstance } from "@/libs/axios";
import { PUBLIC_PROFILE_ENDPOINTS } from "@/constants/api.ts";
import { usePublicProfileStore } from "@/features/profile/public/store/usePublicProfileStore.tsx";

// Define the form schema with Zod
const reviewFormSchema = (t: any) =>
  z.object({
    message: z.string().max(300, {
      message: t("forms:errors.reviewMessageMin"),
    }),
    rating: z
      .number()
      .min(1, {
        message: t("forms:errors.reviewRatingReq"),
      })
      .max(5),
    tutor_profile_id: z.string(),
  });

type ReviewFormValues = z.infer<ReturnType<typeof reviewFormSchema>>;

interface ReviewFormProps {
  onCancel: () => void;
}

export function ReviewForm({ onCancel }: ReviewFormProps) {
  const { t } = useTranslation(["profile", "common", "forms"]);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUserStore();
  const { profileId } = useParams();
  const { profileData } = usePublicProfileStore();

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewFormSchema(t)),
    defaultValues: {
      message: "",
      rating: 0,
      tutor_profile_id: profileData?.id,
    },
  });

  if (!profileId) return null;

  const onSubmit = async (data: ReviewFormValues) => {
    setIsSubmitting(true);

    try {
      await axiosApiInstance.post(PUBLIC_PROFILE_ENDPOINTS(profileId).TUTOR.GET_REVIEWS, data);
    } catch (error) {
      console.error("Error submitting review:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-side-accent relative rounded-lg p-4"
      initial={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-start gap-4">
        <Avatar className="bg-primary/10 h-12 w-12 border">
          <AvatarImage src={user?.avatarUrl || ""} />
          <AvatarFallback>{user?.firstName?.substring(0, 2) ?? "UN"}</AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-4">
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <FormField
                  control={form.control}
                  name="rating"
                  render={({ field }) => (
                    <FormItem className="flex flex-col items-start gap-1">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            className="focus:outline-none"
                            type="button"
                            onClick={() => field.onChange(star)}
                            onMouseEnter={() => setHoveredRating(star)}
                            onMouseLeave={() => setHoveredRating(0)}
                          >
                            <Star
                              className={`h-6 w-6 cursor-pointer ${
                                star <= (hoveredRating || field.value)
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-muted-foreground"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        className="bg-background/50 max-h-[400px] min-h-[100px]"
                        maxLength={300}
                        placeholder={t("reviewsSection.form.messagePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button size="icon" title={t("common:cancel")} variant="outline" onClick={onCancel}>
                  <X className="h-4 w-4" />
                </Button>
                <Button className="w-full sm:w-auto" disabled={isSubmitting} type="submit">
                  {isSubmitting ? t("common:submitting") : t("reviewsSection.form.submit")}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </motion.div>
  );
}
